import { parse as parseCookies, serialize as serializeCookie } from 'cookie';

const BROWSER_TIME_ZONE_COOKIE_NAME = 'imgenhancer.browser_time_zone';
const BROWSER_TIME_ZONE_COOKIE_MAX_AGE_SECONDS = 10 * 60;
const BROWSER_TIME_ZONE_COOKIE_PATH = '/';

type HeadersLike =
  | Headers
  | { headers: { get(name: string): string | null } }
  | string
  | null
  | undefined;

function getCookieHeader(input: HeadersLike): string | null {
  if (typeof input === 'string') {
    return input;
  }

  if (!input) {
    return null;
  }

  const headers = input instanceof Headers ? input : input.headers;
  return headers.get('cookie');
}

export function normalizeBrowserTimeZone(
  value: string | null | undefined
): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 96) {
    return null;
  }

  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: trimmed,
      }).resolvedOptions().timeZone || trimmed
    );
  } catch {
    return null;
  }
}

export function getCurrentBrowserTimeZone(): string | null {
  if (typeof Intl === 'undefined') {
    return null;
  }

  try {
    return normalizeBrowserTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  } catch {
    return null;
  }
}

export function createBrowserTimeZoneCookieHeader(params: {
  timeZone: string;
  secure: boolean;
}) {
  return serializeCookie(BROWSER_TIME_ZONE_COOKIE_NAME, params.timeZone, {
    path: BROWSER_TIME_ZONE_COOKIE_PATH,
    sameSite: 'lax',
    secure: params.secure,
    maxAge: BROWSER_TIME_ZONE_COOKIE_MAX_AGE_SECONDS,
    expires: new Date(
      Date.now() + BROWSER_TIME_ZONE_COOKIE_MAX_AGE_SECONDS * 1000
    ),
  });
}

export function setBrowserTimeZoneCookie() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }

  const timeZone = getCurrentBrowserTimeZone();
  if (!timeZone) {
    return null;
  }

  document.cookie = createBrowserTimeZoneCookieHeader({
    timeZone,
    secure: window.location.protocol === 'https:',
  });

  return timeZone;
}

export function readBrowserTimeZoneCookie(input: HeadersLike): string | null {
  const cookieHeader = getCookieHeader(input);
  if (!cookieHeader) {
    return null;
  }

  const cookies = parseCookies(cookieHeader);
  return normalizeBrowserTimeZone(cookies[BROWSER_TIME_ZONE_COOKIE_NAME]);
}

export function appendClearedBrowserTimeZoneCookieHeader(params: {
  headers: Headers;
  secure: boolean;
}) {
  params.headers.append(
    'Set-Cookie',
    serializeCookie(BROWSER_TIME_ZONE_COOKIE_NAME, '', {
      path: BROWSER_TIME_ZONE_COOKIE_PATH,
      sameSite: 'lax',
      secure: params.secure,
      maxAge: 0,
      expires: new Date(0),
    })
  );
}
